import React, { useState, useMemo, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Save, CalendarDays, AlertTriangle } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";

const API = `${window.location.origin}/api`;

/**
 * InvoiceBulkDatesDialog — lets admins correct invoice dates en-masse.
 *
 * Shows one editable date field per invoice (most recent first). Only rows the
 * admin actually edits are PATCH'd on save, so large lists stay cheap. A
 * "Flagged only" toggle hides everything except rows that likely got an
 * auto-assigned "today" date (invoice_date == created_at date OR missing).
 */
export const InvoiceBulkDatesDialog = ({ open, onClose, invoices = [], authHeaders, onSaved }) => {
  const [edits, setEdits] = useState({}); // {invoiceId: "YYYY-MM-DD"}
  const [flaggedOnly, setFlaggedOnly] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) setEdits({});
  }, [open]);

  // A row is "flagged" if the invoice has no recorded date OR the recorded date
  // exactly matches the created_at day — i.e. it's likely an OCR auto-default.
  const rows = useMemo(() => {
    const withMeta = invoices.map((inv) => {
      const current = inv.invoice_date || inv.date || "";
      const createdDay = (inv.created_at || "").slice(0, 10);
      const likelyAutoStamped = !current || (current.slice(0, 10) === createdDay);
      return { inv, current, createdDay, likelyAutoStamped };
    });
    const filtered = flaggedOnly ? withMeta.filter((r) => r.likelyAutoStamped) : withMeta;
    // Newest first, oldest at the bottom — same sort as main list
    return [...filtered].sort((a, b) =>
      (b.current || b.inv.created_at || "").localeCompare(a.current || a.inv.created_at || "")
    );
  }, [invoices, flaggedOnly]);

  const setDate = (id, val) => setEdits((p) => ({ ...p, [id]: val }));
  const dirtyCount = Object.keys(edits).filter((id) => {
    const target = rows.find((r) => r.inv.id === id)?.current;
    return edits[id] && edits[id] !== target;
  }).length;

  const save = async () => {
    const ids = Object.keys(edits).filter((id) => edits[id]);
    if (!ids.length) { toast.info("No changes to save"); return; }
    setSaving(true);
    let ok = 0; let fail = 0;
    for (const id of ids) {
      try {
        await axios.patch(`${API}/admin/invoices/${id}`, {
          invoice_date: edits[id],
          date: edits[id],
        }, authHeaders);
        ok += 1;
      } catch (e) {
        console.error("[bulk-dates] failed for", id, e);
        fail += 1;
      }
    }
    setSaving(false);
    if (fail === 0) toast.success(`Updated ${ok} invoice date${ok === 1 ? "" : "s"}`);
    else toast.warning(`Updated ${ok}, ${fail} failed`);
    onSaved?.();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-3xl" data-testid="invoice-bulk-dates-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5" />
            Fix invoice dates
          </DialogTitle>
          <DialogDescription>
            Correct invoice dates in bulk. We've flagged rows that look like they picked up today's date from the OCR default — toggle the filter to review all invoices.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 flex-wrap pb-2 border-b">
          <Button
            size="sm"
            variant={flaggedOnly ? "default" : "outline"}
            onClick={() => setFlaggedOnly(true)}
            data-testid="bulk-dates-filter-flagged"
          >
            <AlertTriangle className="h-3.5 w-3.5 mr-1" />
            Flagged only ({invoices.filter((i) => !i.invoice_date || (i.invoice_date || i.date || "").slice(0, 10) === (i.created_at || "").slice(0, 10)).length})
          </Button>
          <Button
            size="sm"
            variant={!flaggedOnly ? "default" : "outline"}
            onClick={() => setFlaggedOnly(false)}
            data-testid="bulk-dates-filter-all"
          >
            All ({invoices.length})
          </Button>
          <div className="ml-auto text-xs text-muted-foreground">
            {dirtyCount > 0 ? `${dirtyCount} unsaved change${dirtyCount === 1 ? "" : "s"}` : "No changes yet"}
          </div>
        </div>

        {rows.length === 0 ? (
          <div className="text-sm text-muted-foreground text-center py-8" data-testid="bulk-dates-empty">
            {flaggedOnly ? "No invoices look auto-stamped — switch to 'All' to edit any invoice." : "No invoices to show."}
          </div>
        ) : (
          <div className="space-y-1 max-h-[55vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-[1fr_auto_180px] gap-2 text-xs font-medium text-muted-foreground px-2">
              <span>Invoice</span>
              <span>Current</span>
              <span>New date</span>
            </div>
            {rows.map(({ inv, current, createdDay, likelyAutoStamped }) => {
              const edited = edits[inv.id];
              const value = edited ?? current ?? "";
              return (
                <div
                  key={inv.id}
                  className="grid grid-cols-[1fr_auto_180px] gap-2 items-center p-2 border rounded hover:bg-muted/30"
                  data-testid={`bulk-dates-row-${inv.id}`}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">
                      {inv.reference || inv.invoice_number || inv.id?.slice(0, 8)}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {inv.customer_name || "—"} · £{Number(inv.total || 0).toFixed(2)}
                      {likelyAutoStamped && (
                        <Badge variant="outline" className="ml-2 border-amber-300 bg-amber-50 text-amber-800 text-[10px]">
                          likely auto-stamped
                        </Badge>
                      )}
                    </p>
                  </div>
                  <div className="text-xs text-muted-foreground whitespace-nowrap">
                    {(current || createdDay || "").slice(0, 10) || "—"}
                  </div>
                  <Input
                    type="date"
                    value={(value || "").slice(0, 10)}
                    onChange={(e) => setDate(inv.id, e.target.value)}
                    className="h-8 text-xs"
                    data-testid={`bulk-dates-input-${inv.id}`}
                  />
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving || dirtyCount === 0} data-testid="bulk-dates-save-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
            Save {dirtyCount > 0 ? `(${dirtyCount})` : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InvoiceBulkDatesDialog;
